import { Button } from "@circleco/compass/components/Button";
import { Select } from "@circleco/compass/components/Select";

import { t } from "@/i18n-js/instance";
import { MasqueradeEntityIcon } from "@/react/components/MasqueradeBanner/MasqueradeEntityIcon";
import {
  entityKey,
  entityOption,
  useSelectedEntityOption,
  withCurrentEntity,
} from "@/react/components/MasqueradeBanner/pickerHelpers";
import type { MasqueradePickerProps } from "@/react/components/MasqueradeBanner/types";
import { classNames } from "@/react/helpers/twMergeWithCN";

import styles from "./picker.module.css";

export const MasqueradeMemberPicker = ({
  currentEntity,
  isError,
  isLoading,
  isSwitching,
  onOpenChange,
  onQueryChange,
  onRetry,
  onSelect,
  open,
  query,
  records,
}: MasqueradePickerProps) => {
  const members = records.filter(entity => entity.type === "CommunityMember");
  const browseMembers = withCurrentEntity(members, currentEntity);
  const visibleMembers = (query.trim() ? members : browseMembers).slice(0, 5);
  const selectedOption = useSelectedEntityOption(currentEntity);

  return (
    <div
      id="masquerade-member-picker"
      className={classNames("min-w-0 w-[266px] max-w-full", styles.picker)}
    >
      <Select
        searchable
        icon="magnifying-glass"
        aria-label={t("masquerade.banner.title")}
        placeholder={t("view_only_masquerading.empty_search_placeholder")}
        initialMessage={
          isError
            ? t("view_only_masquerading.picker.load_error")
            : t("view_only_masquerading.picker.no_matches")
        }
        value={selectedOption}
        inputValue={open ? query : (currentEntity?.name ?? "")}
        options={isLoading || isError ? [] : visibleMembers.map(entityOption)}
        filter={null}
        isItemEqualToValue={(option, value) => option.value === value.value}
        disabled={isSwitching}
        startContent={
          !open && currentEntity ? (
            <MasqueradeEntityIcon entity={currentEntity} />
          ) : undefined
        }
        isLoading={isLoading}
        emptyMessage={
          isError
            ? t("view_only_masquerading.picker.load_error")
            : t("view_only_masquerading.picker.no_matches")
        }
        i18n={{ loadingMessage: t("view_only_masquerading.picker.loading") }}
        open={open}
        align="start"
        dropdownMaxWidth={335}
        onOpenChange={onOpenChange}
        onInputValueChange={(value, details) => {
          if (
            details.reason === "input-change" ||
            details.reason === "clear-press"
          ) {
            const inputEvent = details.event;
            const nextQuery =
              !open && inputEvent instanceof InputEvent
                ? (inputEvent.data ??
                  inputEvent.dataTransfer?.getData("text/plain") ??
                  "")
                : value;
            onQueryChange(nextQuery);
            onOpenChange(true);
          }
        }}
        onValueChange={option => {
          const entity = visibleMembers.find(
            member => entityKey(member) === option?.value,
          );
          if (entity) onSelect(entity);
        }}
      />
      {isError && (
        <Button
          variant="ghost"
          size="sm"
          disabled={isLoading || isSwitching}
          onClick={onRetry}
        >
          {t("view_only_masquerading.picker.retry")}
        </Button>
      )}
    </div>
  );
};
